import { useEffect, useRef } from 'react';
import { AuthStatus } from 'scbk-services/auth/constants';
import useAuthStatus from 'scbk-services/auth/hooks/useAuthStatus';
import { useRedirect } from 'scbk-services/router/hooks/useRedirect';
import { RedirectOptions, RedirectTo } from 'scbk-services/types/route/route';
import { PageComponentType } from '../../types/page/page';
import { meetsAuthRequirement } from '../components/AuthRoute';

/**
 * 권한체크 옵션
 */
export interface WithAuthOptions {
    requiredAuthLevel?: AuthStatus;
    redirect?: {
        to: RedirectTo;
        options?: RedirectOptions;
    };
}

/**
 * 화면 진입 사용자 권한 체크 HOC
 */
export function withAuth<P extends {}>(
    options: WithAuthOptions,
    Page: PageComponentType<P>,
): PageComponentType<P> {
    const Wrapped: React.FC<P> = (props: P) => {
        const { getStatus: getAuthStatus } = useAuthStatus();
        const { redirectToLogin } = useRedirect();
        const hasRedirectedRef = useRef<boolean>(false);

        const hasPermission = meetsAuthRequirement(
            getAuthStatus(),
            options.requiredAuthLevel ?? AuthStatus.UNAUTHENTICATED,
        );

        useEffect(() => {
            if (!hasPermission) {
                if (hasRedirectedRef.current) {
                    return;
                }

                //로그인 페이지로 리다이렉션 처리
                hasRedirectedRef.current = true;
                redirectToLogin({ cause: 'needLogin' });
                return;
            }
        }, [hasPermission, redirectToLogin]);

        if (!hasPermission) {
            return null;
        }

        return <Page {...props} />;
    };

    Wrapped.displayName = `WithAuth(${Page.displayName || Page.name})`;
    return Wrapped;
}
